import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import ConsoleTable, { DataListProps } from "../-components/ConsoleTable";
import Pagination from "components/Pagination";
import { makeIntComma, RouteTypes, shortcut, toastErrorSyle } from "hooks/utils/util";
import { useGetConsoleSupplies } from "hooks/console/useGetConsoleSupplies";
import { useDeleteConsoleSupplies } from "hooks/console/useDeleteConsoleSupplies";
import PageHeader from "../-components/PageHeader";
import { usePutConsoleSuppliesStatus } from "hooks/console/usePutConsoleSuppliesStatus";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import ConfirmDialog from "components/popup/ConfirmDialog";
import Seo from "components/Seo";

const theadStyle = "text-[#222222] text-[16px] text-center";

export default function ConsoleSupplies({ type }: { type: RouteTypes }) {
    const initialSearchForm = {
        category: type,
        search: "subject",
        searchtxt: "",
        page: 1,
        limit: "10",
    };
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [searchForm, setSearchForm] = useState(initialSearchForm);
    const location = useLocation();
    const { data, refetch, isLoading } = useGetConsoleSupplies(searchForm);
    const { mutate: deleteSupplies } = useDeleteConsoleSupplies();
    const { mutate: updateSuppliesStatus } = usePutConsoleSuppliesStatus();
    const allCheckRef = useRef<HTMLInputElement | null>(null);
    const { 운송장및부자재요청 } = shortcut();

    // 사이드바 링크 재 클릭시 상태 초기화
    useEffect(() => {
        if (location.state?.reset) {
            // 초기화 로직 수행
            setSearchForm(initialSearchForm);
        }
    }, [location.state]);

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        if (page) {
            setSearchForm(prev => ({ ...prev, page: Number(page) }));
        }
    }, [searchParams]);

    const deleteRow = () => {
        deleteSupplies(
            { category: searchForm.category, list_no: selectedRows.map(Number) },
            {
                onSuccess: () => {
                    toast.success("성공적으로 삭제 되었습니다.");
                    refetch();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    };

    const toggleRow = (id: number) => {
        const allIdxs = data?.data_list.map(item => item.list_no);

        setSelectedRows(prev => {
            if (prev.includes(id)) {
                // 이미 선택된 경우 제거
                const newRows = prev.filter(rowId => rowId !== id);
                if (newRows.length !== allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            } else {
                // 없으면 추가
                const newRows = [...prev, id];
                if (newRows.length === allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            }
        });
    };

    const allCheck = (checked: boolean) => {
        const allIdxs = data?.data_list.map(item => item.list_no);
        if (checked) {
            if (allIdxs) setSelectedRows(allIdxs);
        } else {
            setSelectedRows([]);
        }
    };

    function handleChangeRowStatus(row: DataListProps, value: string) {
        updateSuppliesStatus(
            {
                category: type,
                list_no: row.list_no,
                status: value,
            },
            {
                onSuccess: () => {
                    toast.success("성공적으로 수정 되었습니다.");
                    refetch();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    }

    return (
        <>
            <Seo type="console" pageTitle="운송장 및 부자재 요청" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="운송장 및 부자재 요청" linkTo={운송장및부자재요청} />

                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-y-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[900px]">
                            <form>
                                <div className="flex gap-2 items-center justify-end">
                                    <Select
                                        value={searchForm.limit}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, limit: value }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[150px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="10">10개</SelectItem>
                                            <SelectItem value="20">20개</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    {/* <Select value="subject" onValueChange={value => setSearchForm(prev => ({ ...prev, search: value }))}>
                                                <SelectTrigger
                                                    className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[120px] border border-[#E1E2E4] bg-white h-[50px]`}
                                                >
                                                    <SelectValue placeholder="" />
                                                    <ArrowDown className="[&_path]:fill-[#666666]" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white">
                                                    <SelectItem value="subject">제목</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <div className="flex items-center border border-[#dddddd] rounded-[5px] bg-white overflow-hidden w-[320px] px-[16px]">
                                                <input
                                                    type="text"
                                                    placeholder="검색어를 입력해주세요."
                                                    className={`text-[16px] placeholder:text-[#878A93] w-full focus:outline-none py-[12px]`}
                                                />
                                                <ReadingGlasses />
                                            </div> */}
                                </div>
                            </form>

                            <div className="mt-[8px] mb-[40px]">
                                <div className="bg-[#DCE1EC] flex justify-between px-[30px] py-[20px] rounded-[5px]">
                                    <div className="flex items-center gap-[30px]">
                                        <label htmlFor="all" className="flex items-center gap-x-2">
                                            <input type="checkbox" id="all" onChange={e => allCheck(e.target.checked)} ref={allCheckRef} />
                                            <span>전체선택</span>
                                        </label>

                                        <label htmlFor="" className="text-[#222222] text-[18px]">
                                            선택한 게시글 총 <span className="text-[#4F46E5]">{selectedRows.length}</span>개
                                        </label>
                                    </div>

                                    <ConfirmDialog
                                        title="요청 삭제"
                                        trigger={open => (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (selectedRows.length === 0) {
                                                        toast.error("삭제할 게시글을 선택해주세요.", toastErrorSyle);
                                                        return;
                                                    }
                                                    open();
                                                }}
                                                className="bg-[#FF5463] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px]"
                                            >
                                                삭제
                                            </button>
                                        )}
                                        handleConfirm={deleteRow}
                                    />
                                </div>

                                <ConsoleTable
                                    thead={[
                                        {
                                            title: "NO",
                                            width: "8%",
                                            key: "index",
                                            content: (row: DataListProps, rowIndex) => {
                                                const index = data!.total_count - (searchForm.page - 1) * 10 - rowIndex;
                                                return (
                                                    <label
                                                        htmlFor={`list-${row.list_no}`}
                                                        className="flex items-center justify-center gap-x-1 px-2 flex-wrap"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            id={`list-${row.list_no}`}
                                                            checked={selectedRows.includes(row.list_no)}
                                                            onChange={() => toggleRow(row.list_no)}
                                                        />
                                                        <p className="break-all text-center">{makeIntComma(index) ?? 0}</p>
                                                    </label>
                                                );
                                            },
                                        },
                                        {
                                            title: "제목",
                                            width: "42%",
                                            key: "subject",
                                            className: twMerge(theadStyle, "text-left px-5"),
                                            // content: (row: DataListProps) => (
                                            //     <Link to={`${row.list_no}`} className="hover:underline">
                                            //         {row.subject}
                                            //     </Link>
                                            // ),
                                        },
                                        {
                                            title: "작성자",
                                            width: "20%",
                                            key: "name",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                        },
                                        {
                                            title: "작성일",
                                            width: "15%",
                                            key: "w_date",
                                            className: twMerge(theadStyle, "text-[#666666] text-[14px]"),
                                        },
                                        {
                                            title: "접수현황",
                                            width: "15%",
                                            key: "status",
                                            content: (row: DataListProps) => (
                                                <Select value={row.status} onValueChange={value => handleChangeRowStatus(row, value)}>
                                                    <SelectTrigger
                                                        className={`relative text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2  border-none shadow-none max-w-[120px] mx-auto justify-center ${row.status === "Y" ? "text-[#4F46E5]" : "text-[#999999]"}`}
                                                    >
                                                        <SelectValue placeholder="" />
                                                        <ArrowDown className="[&_path]:fill-[#1C1B1F]" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-white">
                                                        <SelectItem value="Y">처리완료</SelectItem>
                                                        <SelectItem value="N">접수중</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            ),
                                            className: theadStyle,
                                        },
                                    ]}
                                    tbody={data?.data_list || []}
                                    uniqueKeyName="list_no"
                                    isLoading={isLoading}
                                    trClassName="hover:bg-[#f8f8f8] cursor-pointer"
                                    trClick={row => navigate(`${row.list_no}`)}
                                />
                            </div>

                            <Pagination
                                current={searchForm.page}
                                total={data?.total_count || 1}
                                pageSize={Number(searchForm.limit)}
                                onChange={newPage => {
                                    searchParams.set("page", String(newPage));
                                    setSearchParams(searchParams);
                                    // setSearchForm(prev => ({ ...prev, page: newPage }))
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
